import { useState } from 'react';

import { IconInfoCircleOutline } from '@iwoca/icons';
import { Label, Link, Pill, TextArea } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyQuestionAI.module.css';
import {
  TCompanyQuestionRequest,
  useCompanyQuestionMutation,
} from '../../api/apiCompanyQuestion/apiCompanyQuestionMutation';
import { useGetApplicationQuery } from '../../api/lendingApi/lendingApiQueries';
import { useOnce } from '../../hooks/useOnce';
import { Button } from '../Button/Button';

type TCompanyQuestionAIData = {
  answer: string;
  sicCode: number[];
};

type TQuestionHistory = {
  id: number;
  question: string;
  answer: string;
};

type TSessionData = {
  questions: TQuestionHistory[];
};

// Session storage utilities
const getSessionStorageKey = (applicationId: string) =>
  `company_questions_${applicationId}`;

const getSessionData = (applicationId: string): TSessionData => {
  try {
    const data = sessionStorage.getItem(getSessionStorageKey(applicationId));
    return data ? JSON.parse(data) : { questions: [] };
  } catch {
    return { questions: [] };
  }
};

const setSessionData = (applicationId: string, data: TSessionData): void => {
  sessionStorage.setItem(
    getSessionStorageKey(applicationId),
    JSON.stringify(data),
  );
};

const normaliseCompanyQuestion = (
  data: TCompanyQuestionAIData,
  history: TQuestionHistory[],
): TCompanyQuestionRequest => {
  return {
    context: {
      sic_code: data.sicCode || [],
    },
    history,
  };
};

const CompanyQuestionAI = () => {
  const [textCount, setTextCount] = useState(0);
  const [questionHistory, setQuestionHistory] = useState<TQuestionHistory[]>(
    [],
  );
  // TODO: @w.tong - temp fix first question to be modified to call on load.
  const [currentQuestion, setCurrentQuestion] = useState(
    'What does your company do?',
  );

  const methods = useForm<TCompanyQuestionAIData>();
  const { application } = useGetApplicationQuery();
  const { submitCompanyQuestion, isLoading } = useCompanyQuestionMutation();

  const { handleSubmit, register, reset } = methods;

  useOnce({
    fn: () => {
      if (!application?.application_id) return;

      const sessionData = getSessionData(application.application_id);
      setQuestionHistory(sessionData.questions);
    },
    conditionFn: Boolean(application?.application_id),
  });

  const onSubmit = async (data: TCompanyQuestionAIData) => {
    if (!application?.application_id) {
      return;
    }

    const updatedQuestionCount = questionHistory.length + 1;

    try {
      const newQuestionEntry: TQuestionHistory = {
        id: updatedQuestionCount,
        question: currentQuestion,
        answer: data.answer,
      };

      const updatedHistory = [...questionHistory, newQuestionEntry];

      const response = await submitCompanyQuestion({
        applicationId: application.application_id,
        data: normaliseCompanyQuestion(data, updatedHistory),
      });

      if (!response) throw new Error('No questions populated');

      setQuestionHistory(updatedHistory);

      setSessionData(application.application_id, {
        questions: updatedHistory,
      });

      if (response.inference_question) {
        setCurrentQuestion(response.inference_question);
        reset();
        setTextCount(0);
      }
    } catch (e) {
      throw new Error('Failed fetch', e as Error);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <Pill>Powered by AI</Pill>
        <h2 className={styles.heading}>
          Tell us a bit more about your business
        </h2>
        <p className={styles.description}>
          This will help us understand your business better and more accurately
          assess your application.
        </p>
        <div className={styles.info}>
          <IconInfoCircleOutline
            data-testid="infoIcon"
            width={16}
            height={16}
            className={styles.infoIcon}
          />
          <p className={styles.infoDescription}>
            There are a maximum of five AI generated questions and they should
            take around two minutes to complete.
          </p>
        </div>

        {questionHistory.length > 0 && (
          <div className={styles.previousQuestions}>
            {questionHistory.map((history) => (
              <div key={history.id} className={styles.previousQuestion}>
                <p className={styles.previousQuestion}>{history.question}</p>
                <p className={styles.previousAnswer}>{history.answer}</p>
              </div>
            ))}
          </div>
        )}

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formFieldContainer}>
              <div className={styles.formField}>
                <Label htmlFor="aiQuestion1">{currentQuestion}</Label>
                <TextArea
                  id="aiQuestion1"
                  placeholder="Type your answer here..."
                  maxLength={300}
                  className={styles.questionTextArea}
                  {...register('answer', { required: true })}
                  onChange={(e) => setTextCount(e.currentTarget.value.length)}
                />
              </div>
              <p
                data-testid="character-count"
                className={styles.characterCount}
              >
                {textCount} / 300 characters
              </p>
            </div>
            <Button
              className={styles.nextQuestionBtn}
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            >
              Continue
            </Button>
            {questionHistory.length < 1 && (
              <p
                data-testid="ai-privacy-policy"
                className={styles.privacyPolicy}
              >
                By clicking “Continue” you agree to our{' '}
                <Link
                  href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                  className={styles.privacyPolicyLink}
                >
                  privacy policy
                </Link>
                .
              </p>
            )}
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export default CompanyQuestionAI;
